import React, { useEffect, useState } from 'react';
import { Variant } from '../utils/variant';
import { fireAnalyticsEvent, submitInstallInterest } from '../utils/analytics';
import { isInstallPromptAvailable, triggerInstallPrompt } from '../utils/installPrompt';
import './InstallIntentModal.css';

type Platform = 'native-prompt' | 'ios-safari' | 'unsupported';

function detectPlatform(): Platform {
  if (isInstallPromptAvailable()) return 'native-prompt';
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  if (isIOS && isSafari) return 'ios-safari';
  return 'unsupported';
}

interface Props {
  variant: Variant;
  onClose: () => void;
  onPlayNow: () => void;
}

const InstallIntentModal: React.FC<Props> = ({ variant, onClose, onPlayNow }) => {
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const platform = detectPlatform();

  const handleClose = () => {
    fireAnalyticsEvent('install_modal_dismissed');
    onClose();
  };

  const handleAddToHomeScreen = async () => {
    fireAnalyticsEvent('install_modal_addtohomescreen_clicked');
    const outcome = await triggerInstallPrompt();
    if (outcome === 'accepted') {
      onClose();
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) return;
    setEmailStatus('sending');
    try {
      const res = await submitInstallInterest(email, variant);
      if (res.ok) {
        setEmailStatus('sent');
        fireAnalyticsEvent('install_modal_email_submitted');
      } else {
        setEmailStatus('error');
      }
    } catch {
      setEmailStatus('error');
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="install-modal-overlay" onClick={handleClose}>
      <div className="install-modal" onClick={(e) => e.stopPropagation()}>
        <button className="install-modal-close" onClick={handleClose} aria-label="Close">×</button>

        <h2 className="install-modal-headline">We're working on the app.</h2>
        <p className="install-modal-subhead">
          Meanwhile, play the full game in your browser.
        </p>

        <button className="install-modal-play" onClick={onPlayNow}>
          Play Now
        </button>

        <div className="install-modal-secondary">
          <form className="install-modal-email" onSubmit={handleEmailSubmit}>
            <label htmlFor="install-email">Get notified when the app is ready</label>
            <div className="install-modal-email-row">
              <input
                id="install-email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={emailStatus === 'sending' || emailStatus === 'sent'}
              />
              <button type="submit" disabled={emailStatus === 'sending' || emailStatus === 'sent'}>
                {emailStatus === 'sent' ? 'Thanks!' : emailStatus === 'sending' ? '…' : 'Notify me'}
              </button>
            </div>
            {emailStatus === 'error' && <span className="install-modal-error">Something went wrong. Try again.</span>}
          </form>

          {platform === 'native-prompt' && (
            <div className="install-modal-install">
              <p>Or add Percy to your home screen now:</p>
              <button className="install-modal-secondary-button" onClick={handleAddToHomeScreen}>
                Add to home screen
              </button>
            </div>
          )}

          {platform === 'ios-safari' && (
            <details className="install-modal-install">
              <summary>How to add Percy to your iPhone home screen</summary>
              <ol>
                <li>Tap the <strong>Share</strong> button at the bottom of Safari</li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong></li>
                <li>Tap <strong>Add</strong> in the top-right corner</li>
              </ol>
            </details>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallIntentModal;
